import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityFeedRow } from '@/activities/ActivityFeedRow';
import api from '@/api';
import { AlertBox } from '@/common/AlertBox';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { LoadMoreWrapper, TimelineWrapper } from '@/common/timelineStyles';
import { WBSButton } from '@/common/WBSButton';
import { GroupHeaderWrapper, Title } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/activity',
)({
  component: RouteComponent,
});

/**
 * Legacy's page size, sent explicitly: the shared `PaginateResults` default is
 * 10 and belongs to every other list (`patterns/pagination/dependencies.py:18`),
 * while this tab pages 20 at a time behind Load More
 * (`bp-activity-template.php:255-267`, R12).
 */
const PAGE_SIZE = 20;

function RouteComponent(): ReactElement {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();

  // The slug -> id hop. This route is the one group tab that never resolved the
  // group before, so it inherits the same two-query race the siblings document
  // (`$groupName.students.tsx:35`).
  const { data: group, isError: groupFailed } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  // `enabled` cannot guard the parameters object — qraft takes `parameters` as a
  // required positional argument evaluated before the options are read, and
  // `group` is undefined until the slug resolves. The house sentinel (0, never a
  // real id) is what eight sibling routes use; `group!.id` would throw on the
  // refusal path below.
  const groupId = group?.id ?? 0;

  const {
    data,
    isError: feedFailed,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = api.listGroupActivity.useInfiniteQuery(
    { path: { group_id: groupId }, query: { limit: PAGE_SIZE } },
    {
      enabled: groupId > 0,
      initialPageParam: { query: { offset: 0 } },
      // `PaginatedResponse` carries no `hasMore`, so it is derived from the
      // count it does carry. Offset is the number of rows already held rather
      // than page-index * size: the endpoint's own tiebreak keeps the window
      // stable, and 28.8% of real rows share a timestamp with a sibling.
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((n, page) => n + page.data.length, 0);
        return loaded < lastPage.count
          ? { query: { offset: loaded } }
          : undefined;
      },
    },
  );

  const items = data?.pages.flatMap((page) => page.data) ?? [];
  // A 403 or 404 on either query resolves the screen. Nothing above this route
  // catches a query error (no `errorComponent` anywhere in `web/app/routes`), so
  // without this arm a refused caller sits on a spinner forever: the feed query
  // stays disabled when the slug read fails, and the D24 follow-up-group 404
  // arrives on the feed query itself.
  const refused = groupFailed || feedFailed;
  // Held frame: four e2e specs use this page as a paint anchor and every social
  // group lands here (R14), so a pending read shows the heading and a spinner,
  // never the empty state — "no activity found" would assert the group is empty
  // before anything has been read, and for a refusal it would answer the very
  // question the gate is declining to answer.
  const pending = !refused && (group === undefined || data === undefined);

  return (
    <div>
      <GroupHeaderWrapper>
        <Title>{t('sidebar.activity-log')}</Title>
      </GroupHeaderWrapper>
      {refused ? (
        // Legacy's own literal for this case: a non-member of a hidden group
        // gets `Page not found`, and the key exists in all four locales.
        <AlertBox variant="error">{t('wbs.page-not-found')}</AlertBox>
      ) : pending ? (
        <LoadingSpinner centered />
      ) : items.length === 0 ? (
        // Legacy renders this for an empty group and for an exhausted feed
        // alike (`bp-feedback bp-messages info`).
        <AlertBox variant="info">{t('wbs.sorry-there-was-no')}</AlertBox>
      ) : (
        <TimelineWrapper>
          {items.map((item) => (
            <ActivityFeedRow key={item.id} item={item} />
          ))}
          {hasNextPage && (
            <LoadMoreWrapper>
              <WBSButton
                wide
                alternate
                isLoading={isFetchingNextPage}
                onClick={() => {
                  void fetchNextPage();
                }}
              >
                {t('wbs.load-more')}
              </WBSButton>
            </LoadMoreWrapper>
          )}
        </TimelineWrapper>
      )}
    </div>
  );
}
